import { Alert, CircularProgress, Stack } from '@mui/material';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, createBrowserRouter, useLocation, type RouteObject } from 'react-router';

import type { ApiClient } from '../api/http';
import { useAuth } from '../auth/AuthProvider';
import type { AuthEnvironment } from '../auth/types';
import { useDataSource } from '../config/capabilities';
import {
  RepositoryProvider,
  useOptionalRepository,
  type RepositoryRegistration,
} from '../data/RepositoryProvider';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { MockDashboardRepository } from '../features/dashboard/mockRepository';
import { createRealDashboardRepository } from '../features/dashboard/realRepository';
import { dashboardRepositoryToken } from '../features/dashboard/repository';
import { AgentDetailPage } from '../features/agents/AgentDetailPage';
import { AgentListPage } from '../features/agents/AgentListPage';
import { MockAgentRepository } from '../features/agents/mockRepository';
import { createRealAgentRepository } from '../features/agents/realRepository';
import { agentRepositoryToken } from '../features/agents/repository';
import { AdminUserDetailPage } from '../features/admin-users/AdminUserDetailPage';
import { AdminUserListPage } from '../features/admin-users/AdminUserListPage';
import { MockAdminUsersRepository } from '../features/admin-users/mockRepository';
import { PermissionProfilesPage } from '../features/admin-users/PermissionProfilesPage';
import { createRealAdminUsersRepository } from '../features/admin-users/realRepository';
import { adminUsersRepositoryToken } from '../features/admin-users/repository';
import { AssignmentChangeSetPage, MemberDetailPage } from '../features/members/MemberDetailPage';
import { MemberListPage } from '../features/members/MemberListPage';
import { MockMemberRepository } from '../features/members/mockRepository';
import { createRealMemberRepository } from '../features/members/realRepository';
import { memberRepositoryToken } from '../features/members/repository';
import { GameMasterPage } from '../features/games/GameMasterPage';
import { MockGamesRepository } from '../features/games/mockRepository';
import { PartnerGamesPanel } from '../features/games/PartnerGamesPanel';
import { createRealGamesRepository } from '../features/games/realRepository';
import { gamesRepositoryToken } from '../features/games/repository';
import { CommissionRuleDetailPage } from '../features/commission-rules/CommissionRuleDetailPage';
import { CommissionRuleListPage } from '../features/commission-rules/CommissionRuleListPage';
import { CommissionRuleWizard } from '../features/commission-rules/CommissionRuleWizard';
import { MockCommissionRulesRepository } from '../features/commission-rules/mockRepository';
import { createRealCommissionRulesRepository } from '../features/commission-rules/realRepository';
import { commissionRulesRepositoryToken } from '../features/commission-rules/repository';
import { CommissionLedgerDetailPage } from '../features/commission-ledger/CommissionLedgerDetailPage';
import { CommissionLedgerPage } from '../features/commission-ledger/CommissionLedgerPage';
import { MockCommissionLedgerRepository } from '../features/commission-ledger/mockRepository';
import { createRealCommissionLedgerRepository } from '../features/commission-ledger/realRepository';
import { commissionLedgerRepositoryToken } from '../features/commission-ledger/repository';
import { WalletDetailPage } from '../features/wallets/WalletDetailPage';
import { WalletListPage } from '../features/wallets/WalletListPage';
import { MockWalletRepository } from '../features/wallets/mockRepository';
import { createRealWalletRepository } from '../features/wallets/realRepository';
import { walletsRepositoryToken } from '../features/wallets/repository';
import { WithdrawalDetailPage } from '../features/withdrawals/WithdrawalDetailPage';
import { WithdrawalListPage } from '../features/withdrawals/WithdrawalListPage';
import { MockWithdrawalRepository } from '../features/withdrawals/mockRepository';
import { createRealWithdrawalRepository } from '../features/withdrawals/realRepository';
import { withdrawalsRepositoryToken } from '../features/withdrawals/repository';
import { WithdrawalControlsPanel } from '../features/withdrawal-controls/WithdrawalControlsPanel';
import { MockWithdrawalControlsRepository } from '../features/withdrawal-controls/mockRepository';
import { createRealWithdrawalControlsRepository } from '../features/withdrawal-controls/realRepository';
import { withdrawalControlsRepositoryToken } from '../features/withdrawal-controls/repository';
import { TransactionDetailPage } from '../features/transactions/TransactionDetailPage';
import { TransactionFailureQueuePage } from '../features/transactions/TransactionFailureQueuePage';
import { TransactionListPage } from '../features/transactions/TransactionListPage';
import {
  MockTransactionGapRepository,
  MockTransactionRepository,
  transactionGapRepositoryToken,
} from '../features/transactions/mockRepository';
import { createRealTransactionRepository } from '../features/transactions/realRepository';
import { transactionRepositoryToken } from '../features/transactions/repository';
import { ApiOperationsPage } from '../features/api-operations/ApiOperationsPage';
import {
  apiOperationsRepositoryToken,
  MockApiOperationsRepository,
} from '../features/api-operations/mockRepository';
import { ReconciliationExceptionDetailPage } from '../features/reconciliation/ReconciliationExceptionDetailPage';
import { ReconciliationPage } from '../features/reconciliation/ReconciliationPage';
import { ReconciliationRunDetailPage } from '../features/reconciliation/ReconciliationRunDetailPage';
import { MockReconciliationRepository } from '../features/reconciliation/mockRepository';
import { createRealReconciliationRepository } from '../features/reconciliation/realRepository';
import { reconciliationRepositoryToken } from '../features/reconciliation/repository';
import { ApprovalDetailPage } from '../features/approvals/ApprovalDetailPage';
import { ApprovalListPage } from '../features/approvals/ApprovalListPage';
import { MockApprovalsRepository } from '../features/approvals/mockRepository';
import { createRealApprovalsRepository } from '../features/approvals/realRepository';
import { approvalsRepositoryToken } from '../features/approvals/repository';
import { AuditDetailPage } from '../features/audit/AuditDetailPage';
import { AuditLogPage } from '../features/audit/AuditLogPage';
import { SecurityEventDetailPage } from '../features/audit/SecurityEventDetailPage';
import { SecurityEventsPage } from '../features/audit/SecurityEventsPage';
import { MockAuditRepository } from '../features/audit/mockRepository';
import { createRealAuditRepository } from '../features/audit/realRepository';
import { auditRepositoryToken } from '../features/audit/repository';
import { PartnerDetailPage } from '../features/partners/PartnerDetailPage';
import { PartnerListPage } from '../features/partners/PartnerListPage';
import { MockPartnerRepository } from '../features/partners/mockRepository';
import { createRealPartnerRepository } from '../features/partners/realRepository';
import { partnerRepositoryToken } from '../features/partners/repository';
import { PartnerCreditPage } from '../features/partner-credit/PartnerCreditPage';
import { PartnerCreditVersionDetailPage } from '../features/partner-credit/PartnerCreditVersionDetailPage';
import { MockPartnerCreditRepository } from '../features/partner-credit/mockRepository';
import { createRealPartnerCreditRepository } from '../features/partner-credit/realRepository';
import { partnerCreditRepositoryToken } from '../features/partner-credit/repository';
import { ReportPage } from '../features/reports/ReportPage';
import { MockReportsRepository } from '../features/reports/mockRepository';
import { createRealReportsRepository } from '../features/reports/realRepository';
import { reportsRepositoryToken } from '../features/reports/repository';
import { ReportExportDetailPage } from '../features/report-exports/ReportExportDetailPage';
import { ReportExportsPage } from '../features/report-exports/ReportExportsPage';
import { MockReportExportsRepository } from '../features/report-exports/mockRepository';
import { createRealReportExportsRepository } from '../features/report-exports/realRepository';
import { reportExportsRepositoryToken } from '../features/report-exports/repository';
import { ProfilePage } from '../features/profile/ProfilePage';
import { MainLayout } from '../layouts/MainLayout';
import { DataSourceBadge } from '../shared/data/DataSourceBadge';
import { PageState } from '../shared/data/PageState';
import { PageHeader } from '../shared/layout/PageHeader';
import { paths } from './paths';
import { sitemap } from './sitemap';
import type { FeatureRoute } from './types';

export interface AppRouteOptions {
  readonly apiClient?: ApiClient;
  readonly buildSha?: string;
  readonly environment: AuthEnvironment;
  readonly environmentLabel: string;
  readonly version?: string;
}

export function createAppRoutes(options: AppRouteOptions): RouteObject[] {
  const partnerFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.partners);
  if (partnerFeature === undefined) throw new Error('Partner feature route is not configured');
  const agentFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.agents);
  if (agentFeature === undefined) throw new Error('Agent feature route is not configured');
  const adminUsersFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.adminUsers);
  if (adminUsersFeature === undefined)
    throw new Error('Backoffice User feature route is not configured');
  const memberFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.members);
  if (memberFeature === undefined) throw new Error('Member feature route is not configured');
  const gamesFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.games);
  if (gamesFeature === undefined) throw new Error('Game feature route is not configured');
  const commissionRulesFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.commissionRules);
  if (commissionRulesFeature === undefined)
    throw new Error('Commission Rules feature route is not configured');
  const commissionLedgerFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.commissionLedger);
  if (commissionLedgerFeature === undefined)
    throw new Error('Commission Ledger feature route is not configured');
  const walletsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.affiliateWallets);
  if (walletsFeature === undefined)
    throw new Error('Affiliate Wallet feature route is not configured');
  const withdrawalsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.affiliateWithdrawals);
  if (withdrawalsFeature === undefined)
    throw new Error('Affiliate Withdrawal feature route is not configured');
  const transactionsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.transactions);
  if (transactionsFeature === undefined)
    throw new Error('Transaction feature route is not configured');
  const transactionFailuresFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.transactionFailures);
  if (transactionFailuresFeature === undefined)
    throw new Error('Transaction Failure feature route is not configured');
  const apiOperationsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.apiOperations);
  if (apiOperationsFeature === undefined)
    throw new Error('API Operations feature route is not configured');
  const reconciliationFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.reconciliation);
  if (reconciliationFeature === undefined)
    throw new Error('Reconciliation feature route is not configured');
  const approvalsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.approvals);
  if (approvalsFeature === undefined) throw new Error('Approvals feature route is not configured');
  const auditFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.audit);
  if (auditFeature === undefined) throw new Error('Audit feature route is not configured');
  const securityEventsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.securityEvents);
  if (securityEventsFeature === undefined)
    throw new Error('Security Events feature route is not configured');
  const partnerCreditFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.partnerCredit);
  if (partnerCreditFeature === undefined)
    throw new Error('Partner Credit feature route is not configured');
  const reportsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.reports);
  if (reportsFeature === undefined) throw new Error('Reports feature route is not configured');
  const reportExportsFeature = sitemap
    .flatMap((group) => group.items)
    .find((route) => route.path === paths.reportExports);
  if (reportExportsFeature === undefined)
    throw new Error('Report Exports feature route is not configured');
  return [
    {
      element: <MainLayout {...options} />,
      children: [
        ...sitemap.flatMap((group) =>
          group.items.map((route) => ({
            path: route.path,
            element: (
              <AuthorizedFeature route={route}>
                {route.path === paths.dashboard ? (
                  <DashboardRoute options={options} />
                ) : route.path === paths.partners ? (
                  <PartnerRoute options={options} page="list" />
                ) : route.path === paths.agents ? (
                  <AgentRoute options={options} page="list" />
                ) : route.path === paths.adminUsers ? (
                  <AdminUsersRoute options={options} page="list" />
                ) : route.path === paths.members ? (
                  <MemberRoute options={options} page="list" />
                ) : route.path === paths.games ? (
                  <GamesRoute options={options} page="list" />
                ) : route.path === paths.commissionRules ? (
                  <CommissionRulesRoute options={options} page="list" />
                ) : route.path === paths.commissionLedger ? (
                  <CommissionLedgerRoute options={options} page="list" />
                ) : route.path === paths.affiliateWallets ? (
                  <WalletRoute options={options} page="list" />
                ) : route.path === paths.affiliateWithdrawals ? (
                  <WithdrawalRoute options={options} page="list" />
                ) : route.path === paths.transactions ? (
                  <TransactionRoute options={options} page="list" />
                ) : route.path === paths.transactionFailures ? (
                  <TransactionFailuresRoute options={options} />
                ) : route.path === paths.apiOperations ? (
                  <ApiOperationsRoute options={options} />
                ) : route.path === paths.reconciliation ? (
                  <ReconciliationRoute options={options} page="list" />
                ) : route.path === paths.approvals ? (
                  <ApprovalsRoute options={options} page="list" />
                ) : route.path === paths.audit ? (
                  <AuditRoute options={options} page="list" />
                ) : route.path === paths.securityEvents ? (
                  <SecurityEventsRoute options={options} page="list" />
                ) : route.path === paths.partnerCredit ? (
                  <PartnerCreditRoute options={options} page="landing" />
                ) : route.path === paths.reports ? (
                  <ReportsRoute options={options} />
                ) : route.path === paths.reportExports ? (
                  <ReportExportsRoute options={options} page="list" />
                ) : (
                  route.element
                )}
              </AuthorizedFeature>
            ),
          })),
        ),
        {
          path: '/reports/:reportKind',
          element: (
            <AuthorizedFeature route={reportsFeature}>
              <ReportsRoute options={options} />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.reportExports}/:exportId`,
          element: (
            <AuthorizedFeature route={reportExportsFeature}>
              <ReportExportsRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.partnerCredit}/:partnerId/versions/:versionId`,
          element: (
            <AuthorizedFeature route={partnerCreditFeature}>
              <PartnerCreditRoute options={options} page="version" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.partnerCredit}/:partnerId`,
          element: (
            <AuthorizedFeature route={partnerCreditFeature}>
              <PartnerCreditRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.reconciliation}/runs/:runId`,
          element: (
            <AuthorizedFeature route={reconciliationFeature}>
              <ReconciliationRoute options={options} page="run" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.reconciliation}/exceptions/:exceptionId`,
          element: (
            <AuthorizedFeature route={reconciliationFeature}>
              <ReconciliationRoute options={options} page="exception" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.approvals}/:approvalId`,
          element: (
            <AuthorizedFeature route={approvalsFeature}>
              <ApprovalsRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.audit}/:auditId`,
          element: (
            <AuthorizedFeature route={auditFeature}>
              <AuditRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.securityEvents}/:eventId`,
          element: (
            <AuthorizedFeature route={securityEventsFeature}>
              <SecurityEventsRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.transactions}/:transactionId`,
          element: (
            <AuthorizedFeature route={transactionsFeature}>
              <TransactionRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.affiliateWithdrawals}/:withdrawalId`,
          element: (
            <AuthorizedFeature route={withdrawalsFeature}>
              <WithdrawalRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.affiliateWallets}/:walletId`,
          element: (
            <AuthorizedFeature route={walletsFeature}>
              <WalletRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.commissionLedger}/:entryId`,
          element: (
            <AuthorizedFeature route={commissionLedgerFeature}>
              <CommissionLedgerRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.commissionRules}/new`,
          element: (
            <AuthorizedFeature
              route={{ ...commissionRulesFeature, permission: 'COMMISSION_RULE_MANAGE' }}
            >
              <CommissionRulesRoute options={options} page="new" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.commissionRules}/:ruleId`,
          element: (
            <AuthorizedFeature route={commissionRulesFeature}>
              <CommissionRulesRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: '/game-types/:gameTypeId',
          element: (
            <AuthorizedFeature route={gamesFeature}>
              <GamesRoute options={options} page="gameTypeDetail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.games}/:gameId`,
          element: (
            <AuthorizedFeature route={gamesFeature}>
              <GamesRoute options={options} page="gameDetail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.partners}/:partnerId/games`,
          element: (
            <AuthorizedFeature route={gamesFeature}>
              <GamesRoute options={options} page="partnerGames" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.partners}/:partnerId/withdrawal-controls`,
          element: (
            <AuthorizedFeature
              route={{
                ...partnerFeature,
                capability: 'withdrawalControls',
                permission: 'WITHDRAWAL_VIEW',
              }}
            >
              <WithdrawalControlsRoute options={options} />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.partners}/:partnerId`,
          element: (
            <AuthorizedFeature route={partnerFeature}>
              <PartnerRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.agents}/:agentId`,
          element: (
            <AuthorizedFeature route={agentFeature}>
              <AgentRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.members}/:memberId`,
          element: (
            <AuthorizedFeature route={memberFeature}>
              <MemberRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: '/member-assignment-change-sets/:changeSetId',
          element: (
            <AuthorizedFeature route={{ ...memberFeature, permission: 'MEMBER_MANAGE' }}>
              <MemberRoute options={options} page="changeSet" />
            </AuthorizedFeature>
          ),
        },
        {
          path: `${paths.adminUsers}/:adminUserId`,
          element: (
            <AuthorizedFeature route={adminUsersFeature}>
              <AdminUsersRoute options={options} page="detail" />
            </AuthorizedFeature>
          ),
        },
        {
          path: paths.permissionProfiles,
          element: (
            <AuthorizedFeature route={adminUsersFeature}>
              <AdminUsersRoute options={options} page="profiles" />
            </AuthorizedFeature>
          ),
        },
        {
          path: paths.profile,
          element: (
            <RequireAuthenticated>
              <ProfileRoute options={options} />
            </RequireAuthenticated>
          ),
        },
        { path: '*', element: <Navigate replace to={paths.dashboard} /> },
      ],
    },
  ];
}

function ReportsRoute({ options }: { readonly options: AppRouteOptions }) {
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const injected = useOptionalRepository(reportsRepositoryToken);
  const injectedExports = useOptionalRepository(reportExportsRepositoryToken);
  const dataSource = useDataSource('reports', options.environment);
  const exportDataSource = useDataSource('reportExports', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    return dataSource === 'mock'
      ? new MockReportsRepository()
      : createRealReportsRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);
  const exportsRepository = useMemo(() => {
    if (exportDataSource === 'unavailable') return null;
    if (injectedExports !== null) return injectedExports;
    return exportDataSource === 'mock'
      ? new MockReportExportsRepository()
      : createRealReportExportsRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, exportDataSource, injectedExports, options.apiClient]);
  if (repository === null || exportsRepository === null)
    return <CapabilityUnavailable subtitle={t('reports.subtitle')} title={t('reports.title')} />;
  return (
    <RepositoryProvider
      repositories={[
        [reportsRepositoryToken, repository],
        [reportExportsRepositoryToken, exportsRepository],
      ]}
    >
      <ReportPage dataSource={dataSource} key={`${location.pathname}${location.search}`} />
    </RepositoryProvider>
  );
}

function ReportExportsRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(reportExportsRepositoryToken);
  const dataSource = useDataSource('reportExports', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    return dataSource === 'mock'
      ? new MockReportExportsRepository()
      : createRealReportExportsRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);
  if (repository === null)
    return (
      <CapabilityUnavailable
        subtitle={t('reportExports.subtitle')}
        title={t('reportExports.title')}
      />
    );
  return (
    <RepositoryProvider repositories={[[reportExportsRepositoryToken, repository]]}>
      {page === 'list' ? (
        <ReportExportsPage dataSource={dataSource} />
      ) : (
        <ReportExportDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function PartnerCreditRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'landing' | 'detail' | 'version';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(partnerCreditRepositoryToken);
  const injectedPartners = useOptionalRepository(partnerRepositoryToken);
  const dataSource = useDataSource('partnerCredit', options.environment);
  const healthDataSource = useDataSource('partnerCreditHealth', options.environment);
  const partnerDataSource = useDataSource('partners', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockPartnerCreditRepository();
    return createRealPartnerCreditRepository(
      options.apiClient ?? unavailableApiClient,
      accessToken,
    );
  }, [accessToken, dataSource, injected, options.apiClient]);
  const partners = useMemo(() => {
    if (page !== 'landing') return null;
    if (partnerDataSource === 'unavailable') return null;
    if (injectedPartners !== null) return injectedPartners;
    if (partnerDataSource === 'mock') return new MockPartnerRepository();
    return createRealPartnerRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, injectedPartners, options.apiClient, page, partnerDataSource]);
  if (repository === null || (page === 'landing' && partners === null))
    return (
      <CapabilityUnavailable
        subtitle={t('partnerCredit.subtitle')}
        title={t('partnerCredit.title')}
      />
    );
  const repositories: readonly RepositoryRegistration[] =
    partners === null
      ? [[partnerCreditRepositoryToken, repository]]
      : [
          [partnerCreditRepositoryToken, repository],
          [partnerRepositoryToken, partners],
        ];
  return (
    <RepositoryProvider repositories={repositories}>
      {page === 'version' ? (
        <PartnerCreditVersionDetailPage dataSource={dataSource} />
      ) : (
        <PartnerCreditPage dataSource={dataSource} healthDataSource={healthDataSource} />
      )}
    </RepositoryProvider>
  );
}

function ReconciliationRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'run' | 'exception';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(reconciliationRepositoryToken);
  const dataSource = useDataSource('reconciliation', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockReconciliationRepository();
    return createRealReconciliationRepository(
      options.apiClient ?? unavailableApiClient,
      accessToken,
    );
  }, [accessToken, dataSource, injected, options.apiClient]);
  if (repository === null)
    return (
      <CapabilityUnavailable
        subtitle={t('reconciliation.subtitle')}
        title={t('reconciliation.title')}
      />
    );
  return (
    <RepositoryProvider repositories={[[reconciliationRepositoryToken, repository]]}>
      {page === 'list' ? (
        <ReconciliationPage dataSource={dataSource} />
      ) : page === 'run' ? (
        <ReconciliationRunDetailPage dataSource={dataSource} />
      ) : (
        <ReconciliationExceptionDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function ApprovalsRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(approvalsRepositoryToken);
  const dataSource = useDataSource('approvals', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockApprovalsRepository();
    return createRealApprovalsRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);
  if (repository === null)
    return (
      <CapabilityUnavailable subtitle={t('approvals.subtitle')} title={t('approvals.title')} />
    );
  return (
    <RepositoryProvider repositories={[[approvalsRepositoryToken, repository]]}>
      {page === 'list' ? (
        <ApprovalListPage dataSource={dataSource} />
      ) : (
        <ApprovalDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function AuditRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(auditRepositoryToken);
  const dataSource = useDataSource('audit', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockAuditRepository();
    return createRealAuditRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);
  if (repository === null)
    return <CapabilityUnavailable subtitle={t('audit.subtitle')} title={t('audit.title')} />;
  return (
    <RepositoryProvider repositories={[[auditRepositoryToken, repository]]}>
      {page === 'list' ? (
        <AuditLogPage dataSource={dataSource} />
      ) : (
        <AuditDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function SecurityEventsRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(auditRepositoryToken);
  const dataSource = useDataSource('securityEvents', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockAuditRepository();
    return createRealAuditRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);
  if (repository === null)
    return (
      <CapabilityUnavailable
        subtitle={t('securityEvents.subtitle')}
        title={t('securityEvents.title')}
      />
    );
  return (
    <RepositoryProvider repositories={[[auditRepositoryToken, repository]]}>
      {page === 'list' ? (
        <SecurityEventsPage dataSource={dataSource} />
      ) : (
        <SecurityEventDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function TransactionRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(transactionRepositoryToken);
  const injectedGaps = useOptionalRepository(transactionGapRepositoryToken);
  const dataSource = useDataSource('transactions', options.environment);
  const reversalDataSource = useDataSource('transactionReversal', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockTransactionRepository();
    return createRealTransactionRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);
  const reversalRepository = useMemo(
    () =>
      reversalDataSource === 'mock' ? (injectedGaps ?? new MockTransactionGapRepository()) : null,
    [injectedGaps, reversalDataSource],
  );
  if (repository === null)
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('transactions.subtitle')}
          title={t('transactions.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  return (
    <RepositoryProvider repositories={[[transactionRepositoryToken, repository]]}>
      {page === 'list' ? (
        <TransactionListPage dataSource={dataSource} />
      ) : (
        <TransactionDetailPage
          dataSource={dataSource}
          reversalDataSource={reversalDataSource}
          reversalRepository={reversalRepository}
        />
      )}
    </RepositoryProvider>
  );
}

function TransactionFailuresRoute({ options }: { readonly options: AppRouteOptions }) {
  const { t } = useTranslation();
  const injected = useOptionalRepository(transactionGapRepositoryToken);
  const dataSource = useDataSource('transactionFailures', options.environment);
  const repository = useMemo(
    () => (dataSource === 'mock' ? (injected ?? new MockTransactionGapRepository()) : null),
    [dataSource, injected],
  );
  if (repository === null)
    return (
      <CapabilityUnavailable
        subtitle={t('transactionFailures.subtitle')}
        title={t('transactionFailures.title')}
      />
    );
  return (
    <RepositoryProvider repositories={[[transactionGapRepositoryToken, repository]]}>
      <TransactionFailureQueuePage />
    </RepositoryProvider>
  );
}

function ApiOperationsRoute({ options }: { readonly options: AppRouteOptions }) {
  const { t } = useTranslation();
  const injected = useOptionalRepository(apiOperationsRepositoryToken);
  const dataSource = useDataSource('apiOperations', options.environment);
  const repository = useMemo(
    () => (dataSource === 'mock' ? (injected ?? new MockApiOperationsRepository()) : null),
    [dataSource, injected],
  );
  if (repository === null)
    return (
      <CapabilityUnavailable
        subtitle={t('apiOperations.subtitle')}
        title={t('apiOperations.title')}
      />
    );
  return (
    <RepositoryProvider repositories={[[apiOperationsRepositoryToken, repository]]}>
      <ApiOperationsPage />
    </RepositoryProvider>
  );
}

function CapabilityUnavailable({
  subtitle,
  title,
}: {
  readonly subtitle: string;
  readonly title: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <PageHeader
        actions={<DataSourceBadge source="unavailable" />}
        subtitle={subtitle}
        title={title}
      />
      <Alert severity="warning">{t('capability.backendUnavailable')}</Alert>
    </div>
  );
}

function WithdrawalRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(withdrawalsRepositoryToken);
  const dataSource = useDataSource('withdrawals', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockWithdrawalRepository();
    return createRealWithdrawalRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('withdrawals.subtitle')}
          title={t('withdrawals.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[withdrawalsRepositoryToken, repository]]}>
      {page === 'list' ? (
        <WithdrawalListPage dataSource={dataSource} />
      ) : (
        <WithdrawalDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function WithdrawalControlsRoute({ options }: { readonly options: AppRouteOptions }) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(withdrawalControlsRepositoryToken);
  const dataSource = useDataSource('withdrawalControls', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockWithdrawalControlsRepository();
    return createRealWithdrawalControlsRepository(
      options.apiClient ?? unavailableApiClient,
      accessToken,
    );
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('withdrawalControls.subtitle', { partnerId: '' })}
          title={t('withdrawalControls.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[withdrawalControlsRepositoryToken, repository]]}>
      <WithdrawalControlsPanel dataSource={dataSource} />
    </RepositoryProvider>
  );
}

function WalletRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(walletsRepositoryToken);
  const dataSource = useDataSource('wallets', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockWalletRepository();
    return createRealWalletRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('wallets.subtitle')}
          title={t('wallets.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[walletsRepositoryToken, repository]]}>
      {page === 'list' ? (
        <WalletListPage dataSource={dataSource} />
      ) : (
        <WalletDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function CommissionLedgerRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(commissionLedgerRepositoryToken);
  const dataSource = useDataSource('commissionLedger', options.environment);
  const adjustmentDataSource = useDataSource('commissionAdjustments', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockCommissionLedgerRepository();
    return createRealCommissionLedgerRepository(
      options.apiClient ?? unavailableApiClient,
      accessToken,
    );
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('commissionLedger.subtitle')}
          title={t('commissionLedger.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[commissionLedgerRepositoryToken, repository]]}>
      {page === 'list' ? (
        <CommissionLedgerPage adjustmentDataSource={adjustmentDataSource} dataSource={dataSource} />
      ) : (
        <CommissionLedgerDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function CommissionRulesRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'new' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(commissionRulesRepositoryToken);
  const dataSource = useDataSource('commissionRules', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockCommissionRulesRepository();
    return createRealCommissionRulesRepository(
      options.apiClient ?? unavailableApiClient,
      accessToken,
    );
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('commissionRules.subtitle')}
          title={t('commissionRules.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[commissionRulesRepositoryToken, repository]]}>
      {page === 'list' ? (
        <CommissionRuleListPage dataSource={dataSource} />
      ) : page === 'new' ? (
        <CommissionRuleWizard dataSource={dataSource} />
      ) : (
        <CommissionRuleDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function GamesRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'gameTypeDetail' | 'gameDetail' | 'partnerGames';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(gamesRepositoryToken);
  const dataSource = useDataSource('games', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockGamesRepository();
    return createRealGamesRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('games.subtitle')}
          title={t('games.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[gamesRepositoryToken, repository]]}>
      {page === 'partnerGames' ? (
        <PartnerGamesPanel dataSource={dataSource} />
      ) : (
        <GameMasterPage dataSource={dataSource} view={page} />
      )}
    </RepositoryProvider>
  );
}

function MemberRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail' | 'changeSet';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(memberRepositoryToken);
  const dataSource = useDataSource('members', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockMemberRepository();
    return createRealMemberRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('members.subtitle')}
          title={t('members.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[memberRepositoryToken, repository]]}>
      {page === 'list' ? (
        <MemberListPage dataSource={dataSource} />
      ) : page === 'detail' ? (
        <MemberDetailPage dataSource={dataSource} />
      ) : (
        <AssignmentChangeSetPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function AdminUsersRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail' | 'profiles';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(adminUsersRepositoryToken);
  const dataSource = useDataSource('adminUsers', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockAdminUsersRepository();
    return createRealAdminUsersRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('adminUsers.subtitle')}
          title={t('adminUsers.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[adminUsersRepositoryToken, repository]]}>
      {page === 'list' ? (
        <AdminUserListPage dataSource={dataSource} />
      ) : page === 'detail' ? (
        <AdminUserDetailPage authMode={options.environment.authMode} dataSource={dataSource} />
      ) : (
        <PermissionProfilesPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function AgentRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(agentRepositoryToken);
  const dataSource = useDataSource('agents', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (dataSource === 'unavailable') return null;
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockAgentRepository();
    return createRealAgentRepository(options.apiClient ?? unavailableApiClient, accessToken);
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('agents.subtitle')}
          title={t('agents.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[agentRepositoryToken, repository]]}>
      {page === 'list' ? (
        <AgentListPage dataSource={dataSource} />
      ) : (
        <AgentDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

function PartnerRoute({
  options,
  page,
}: {
  readonly options: AppRouteOptions;
  readonly page: 'list' | 'detail';
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(partnerRepositoryToken);
  const dataSource = useDataSource('partners', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockPartnerRepository();
    if (dataSource === 'real') {
      return createRealPartnerRepository(options.apiClient ?? unavailableApiClient, accessToken);
    }
    return null;
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('partners.subtitle')}
          title={t('partners.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[partnerRepositoryToken, repository]]}>
      {page === 'list' ? (
        <PartnerListPage dataSource={dataSource} />
      ) : (
        <PartnerDetailPage dataSource={dataSource} />
      )}
    </RepositoryProvider>
  );
}

export function createAppRouter(options: AppRouteOptions) {
  return createBrowserRouter(createAppRoutes(options));
}

function AuthorizedFeature({
  children,
  route,
}: {
  readonly children: ReactNode;
  readonly route: FeatureRoute;
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  if (auth.status === 'loading') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CircularProgress size={18} />
        <span>{t('auth.loading')}</span>
      </Stack>
    );
  }
  if (auth.status === 'unauthenticated') {
    return <Alert severity="warning">{t('auth.unauthenticated')}</Alert>;
  }
  if (auth.status === 'error') {
    return <Alert severity="error">{t('auth.error')}</Alert>;
  }
  const requiredPermissions =
    route.capability === 'reportExports' ? [route.permission, 'REPORT_VIEW'] : [route.permission];
  if (!requiredPermissions.every((permission) => auth.principal.permissions.includes(permission))) {
    return <Alert severity="error">{t('auth.accessDenied')}</Alert>;
  }
  return children;
}

// Profile has no dedicated capability/permission: it is available to any authenticated principal
// and is reached only from the Topbar account menu, never the sitemap. This mirrors
// AuthorizedFeature's loading/unauthenticated/error handling but skips the permission check.
function RequireAuthenticated({ children }: { readonly children: ReactNode }) {
  const auth = useAuth();
  const { t } = useTranslation();
  if (auth.status === 'loading') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CircularProgress size={18} />
        <span>{t('auth.loading')}</span>
      </Stack>
    );
  }
  if (auth.status === 'unauthenticated') {
    return <Alert severity="warning">{t('auth.unauthenticated')}</Alert>;
  }
  if (auth.status === 'error') {
    return <Alert severity="error">{t('auth.error')}</Alert>;
  }
  return children;
}

function ProfileRoute({ options }: { readonly options: AppRouteOptions }) {
  const dataSource = useDataSource('authProfile', options.environment);
  return <ProfilePage dataSource={dataSource} />;
}

function DashboardRoute({ options }: { readonly options: AppRouteOptions }) {
  const auth = useAuth();
  const { t } = useTranslation();
  const injected = useOptionalRepository(dashboardRepositoryToken);
  const dataSource = useDataSource('dashboard', options.environment);
  const accessToken = auth.status === 'authenticated' ? auth.accessToken : null;
  const repository = useMemo(() => {
    if (injected !== null) return injected;
    if (dataSource === 'mock') return new MockDashboardRepository();
    if (dataSource === 'real') {
      return createRealDashboardRepository(options.apiClient ?? unavailableApiClient, accessToken);
    }
    return null;
  }, [accessToken, dataSource, injected, options.apiClient]);

  if (repository === null) {
    return (
      <div>
        <PageHeader
          actions={<DataSourceBadge source="unavailable" />}
          subtitle={t('dashboard.subtitle')}
          title={t('dashboard.title')}
        />
        <PageState status="unavailable" />
      </div>
    );
  }

  return (
    <RepositoryProvider repositories={[[dashboardRepositoryToken, repository]]}>
      <DashboardPage dataSource={dataSource} />
    </RepositoryProvider>
  );
}

const unavailableApiClient: ApiClient = {
  download: async () => Promise.reject(new Error('API client is not configured')),
  get: async () => Promise.reject(new Error('API client is not configured')),
  patch: async () => Promise.reject(new Error('API client is not configured')),
  post: async () => Promise.reject(new Error('API client is not configured')),
};
